/**
 * Types for Adventure Files/Media Management
 */

// ===================
// DTOs
// ===================

/**
 * Basic file info returned in list views
 */
export interface AdventureMediaFileDto {
  id: string
  fileName: string
  displayName: string | null
  description: string | null
  mimeType: string
  fileSize: number
  fileUrl: string
  thumbnailUrl: string | null
  width: number | null
  height: number | null
  duration: number | null // For video/audio in seconds
  usageCount: number
  createdDate: string
  modifiedDate: string
}

/**
 * Detailed file info including full metadata
 */
export interface AdventureMediaFileDetailDto extends AdventureMediaFileDto {
  adventureId: string
  uploadedBy: string
  uploadedByName: string
  tags: string[]
  metadata: Record<string, unknown> | null
}

/**
 * Result of a file upload
 */
export interface AdventureMediaUploadResultDto {
  file: AdventureMediaFileDto
  url: string
  thumbnailUrl: string | null
}

/**
 * Represents where a file is used within an adventure
 */
export interface MediaFileUsageDto {
  entityType: 'Node' | 'Description' | 'Criterion' | 'Other'
  entityId: string
  entityName: string
  context: string // e.g., "Image in node content", "Background image"
  usedAt: string
}

/**
 * Result of selecting/linking an existing file to an adventure
 */
export interface AdventureFileSelectionResultDto {
  success: boolean
  file: AdventureMediaFileDto
  message?: string
}

// ===================
// Request Types
// ===================

/**
 * Parameters for listing adventure files
 */
export interface GetAdventureFilesParams {
  page?: number
  pageSize?: number
  searchTerm?: string
  mediaType?: 'all' | 'image' | 'video' | 'audio' | 'document'
  sortBy?: 'name' | 'date' | 'size'
  sortDesc?: boolean
}

/**
 * Request to update file metadata
 */
export interface UpdateAdventureMediaFileRequest {
  displayName?: string
  description?: string
  tags?: string[]
}

/**
 * Request to select/link an existing file to an adventure
 */
export interface SelectAdventureFileRequest {
  fileId: string
  displayName?: string
  description?: string
}

/**
 * Request to set or clear adventure thumbnail/cover image
 */
export interface SetAdventureImageRequest {
  mediaFileId: string | null
  usageContext: 'thumbnail' | 'cover'
}

/**
 * Options for file upload
 */
export interface AdventureFileUploadOptions {
  displayName?: string
  description?: string
  tags?: string[]
}

// ===================
// Response Types
// ===================

/**
 * Paginated response for file listing
 */
export interface AdventureFileListResponse {
  files: AdventureMediaFileDto[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

// ===================
// Utility Functions
// ===================

/**
 * Format file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'

  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const k = 1024
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`
}

/**
 * Get media type category from MIME type
 */
export function getMediaTypeFromMime(mimeType: string): 'image' | 'video' | 'audio' | 'document' | 'other' {
  if (mimeType.startsWith('image/')) return 'image'
  if (mimeType.startsWith('video/')) return 'video'
  if (mimeType.startsWith('audio/')) return 'audio'
  if (
    mimeType.startsWith('application/pdf') ||
    mimeType.startsWith('application/msword') ||
    mimeType.startsWith('application/vnd.') ||
    mimeType.startsWith('text/')
  ) {
    return 'document'
  }
  return 'other'
}

/**
 * Get file type icon name based on MIME type
 */
export function getFileTypeIcon(mimeType: string): 'image' | 'video' | 'audio' | 'document' | 'file' {
  const type = getMediaTypeFromMime(mimeType)
  if (type === 'other') return 'file'
  return type
}

/**
 * Check if file is previewable (image or video)
 */
export function isPreviewable(mimeType: string): boolean {
  return mimeType.startsWith('image/') || mimeType.startsWith('video/')
}

/**
 * Format duration in human-readable format (for video/audio)
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}
